import React, { useCallback, useState } from "react";
import styles from './UploadForm.module.scss';
import { useDropzone } from 'react-dropzone';
import {ReactComponent as FileUploadIcon} from './FIleUpload.svg';
import {ReactComponent as FileTextIcon} from './FileText.svg';
import {ReactComponent as CloseIcon} from './Close.svg';
import {ReactComponent as DownloadIcon} from './Download.svg';
import { formatFileSize } from "../../utils/stringUtils";
import Button from "../button/Button";
import PropTypes from "prop-types";
import ProgressBar from "../progressBar/ProgressBar";
import { useToasts } from "../../contexts/ToastContext";

const UploadForm = ({
  isUploading = false,
  uploadProgress,
  onClose,
  onSubmit,
  buttonLabel="Import",
  templateFileUrl
}) => {
  const [file, setFile] = useState(null);
  const { showToast } = useToasts();

  const onDrop = useCallback((acceptedFiles) => {
    const uploadedFile = acceptedFiles[0];
    setFile(uploadedFile);
    // Handle the file upload logic here (e.g., send to server)
  }, []);

  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' : ['xlsx']
    },  // Only accept .xlsx files
    multiple: false,  // Allow only one file at a time
  });

  const handleDownload = () => { 
    const downloadUrl = templateFileUrl
    // Create a temporary <a> tag to trigger the download
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.target = "_blank";
    link.download = "Template-File.xlsx"; // Ensure the correct file extension
    link.click(); // Trigger the download
  };

  const handleSubmit = () => {
    if(!file) {
      showToast('Please select file to upload', 'error');
      return false;
    }
    onSubmit(file);
  }

  return (
    <form
      className={styles.form}
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit();
      }}
    >
      <div
        className={styles.dragBox}
        {...getRootProps()}
        style={{
          backgroundColor: isDragActive ? '#FFF' : '#F9F9FF',
          borderColor: isDragReject ? '#E03030' : isDragActive ? '#171898' : '#1A68DD',
        }}
      >
        {isUploading ?
          <>
            <div className={styles.uploadingBox}>
              <FileTextIcon width={'56px'} height={'56px'} />
              <div className={styles.progressBarBox}>
                <ProgressBar percentage={uploadProgress} />
              </div>
            </div>
            <p>Uploading File...</p>
          </> : 
          (file ?
            <>
              <CloseIcon
                className={styles.close}
                onClick={() => setFile(null)}
              />
              <div className={styles.selectedFile}>
                <FileTextIcon />
                <div className={styles.details}>
                  <span className={styles.name}>{file.name}</span>
                  <span className={styles.size}>{formatFileSize(file.size)}</span>
                </div>
              </div>
            </> :
            <>
              <FileUploadIcon />
              <input {...getInputProps()} />
              <p className={styles.message}>
                {isDragActive
                  ? 'Release to drop the file here'
                  : isDragReject
                  ? 'This file type is not accepted. Please drop an .xlsx file.'
                  : 'Drag & drop an .xlsx file here, or click to select a file'}
              </p>
              <p className={styles.helpText}>
                Maximum File Size : 50 MB<br />
                Supported Format: XLSX
              </p>
            </>)}
      </div>
      <div className={styles.templateBox}>
        <h4>Template File to Download</h4>
        <div className={styles.templateFile}>
          <FileTextIcon width={'32px'} height={'32px'}/>
          <div className={styles.details}>
            <span className={styles.name}>Template File</span>
            <span className={styles.format}>XLSX</span>
          </div>
          <button
            type="button"
            onClick={handleDownload}
          >
            <DownloadIcon /> Download
          </button>
        </div>
      </div>
      <div className={styles.actions}>
        <Button
          label="Close"
          variant="secondary"
          size="medium"
          onClick={onClose}
        />
        <Button
          label={buttonLabel}
          variant="primary"
          size="medium"
          type="submit"
          disabled={isUploading}
        />
      </div>
    </form>
  );
};

UploadForm.propTypes = {
  isUploading: PropTypes.bool,
  uploadProgress: PropTypes.number,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  buttonLabel:PropTypes.string,
  templateFileUrl:PropTypes.string
}

export default UploadForm;